import { useState } from 'react';
import { createPlace } from '../../services/api';
import { PhotoUpload } from '../UI/PhotoUpload';
import { ErrorBanner } from '../UI/ErrorBanner';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import type { NominatimResult, VisitType, AddVisitFormData } from '../../types';

interface AddVisitFormProps {
  nominatimResult: NominatimResult;
  onSuccess: () => void;
  onCancel: () => void;
}

function getCountry(r: NominatimResult): string {
  return r.address.country || '';
}

function getCountryCode(r: NominatimResult): string {
  return (r.address.country_code || '').toUpperCase().slice(0, 2);
}

function getPlaceName(r: NominatimResult): string {
  return r.address.village || r.address.hamlet || r.address.suburb || r.address.town || r.address.city || r.name || r.display_name;
}

export function AddVisitForm({ nominatimResult, onSuccess, onCancel }: AddVisitFormProps) {
  const [placeName, setPlaceName] = useState(() => getPlaceName(nominatimResult));
  const [visitType, setVisitType] = useState<VisitType>('solo');
  const [soloTraveller, setSoloTraveller] = useState<string>('');
  const [visitDate, setVisitDate] = useState('');
  const [notes, setNotes] = useState('');
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const data: AddVisitFormData = {
      nominatimId: nominatimResult.place_id,
      placeName: placeName.trim() || getPlaceName(nominatimResult),
      country: getCountry(nominatimResult),
      countryCode: getCountryCode(nominatimResult),
      latitude: parseFloat(nominatimResult.lat),
      longitude: parseFloat(nominatimResult.lon),
      visitType,
      visitDate,
      notes,
      userImageBlobURL: blobUrl,
      linkedUserIds: [],
      soloTraveller: visitType === 'solo' ? (soloTraveller || null) : null,
    };

    try {
      await createPlace(data);
      onSuccess();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    fontSize: 14,
    marginTop: 4,
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#616161',
    marginTop: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '0 16px 20px' }}>
      <label style={{ ...labelStyle, marginTop: 14 }}>
        Place name
        <input
          type="text"
          value={placeName}
          onChange={(e) => setPlaceName(e.target.value)}
          style={{ ...inputStyle, fontWeight: 600 }}
        />
      </label>
      <p style={{ fontSize: 12, color: '#9e9e9e', margin: '4px 0 0' }}>
        {getCountry(nominatimResult)}
      </p>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <label style={labelStyle}>
        Visit type
        <select value={visitType} onChange={(e) => setVisitType(e.target.value as VisitType)} style={inputStyle}>
          <option value="solo">Solo</option>
          <option value="family">Family</option>
          <option value="group">Group</option>
          <option value="markanya">Mark & Anya</option>
        </select>
      </label>

      {visitType === 'solo' && (
        <label style={labelStyle}>
          Who travelled?
          <select value={soloTraveller} onChange={(e) => setSoloTraveller(e.target.value)} style={inputStyle}>
            <option value="">Select person...</option>
            <option value="Mark">Mark</option>
            <option value="Anya">Anya</option>
            <option value="Victoria">Victoria</option>
            <option value="Liam">Liam</option>
          </select>
        </label>
      )}

      <label style={labelStyle}>
        Date visited
        <input
          type="date"
          value={visitDate}
          onChange={(e) => setVisitDate(e.target.value)}
          style={inputStyle}
        />
      </label>

      <label style={labelStyle}>
        Notes
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="How was it? Any memories to capture?"
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </label>

      <label style={labelStyle}>
        Photo (optional)
      </label>
      <PhotoUpload onUploaded={setBlobUrl} currentUrl={blobUrl} />

      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button
          type="submit"
          disabled={submitting}
          style={{
            flex: 1,
            padding: '10px',
            background: '#2196F3',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? <LoadingSpinner size={16} /> : 'Save Visit'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '10px 16px',
            background: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
